import type { SignalData } from '@terra/ui-design-system';
import type { GroupedSignal } from '@terra/shared-contracts-signals';

const SIGNAL_HITL_TOOL_NAMES = new Set([
  'signal-tool',
  'create_signal',
  'create_signals',
  'update_signal',
]);

type SignalMetadata = {
  hypothesis?: string;
  description?: string;
};

function readSignalMetadata(
  metadata: GroupedSignal['metadata'],
): SignalMetadata {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
    return {};
  }
  const record = metadata as Record<string, unknown>;
  return {
    hypothesis:
      typeof record.hypothesis === 'string' ? record.hypothesis : undefined,
    description:
      typeof record.description === 'string' ? record.description : undefined,
  };
}

export function shouldHydrateSignalHitl({
  toolName,
  args,
}: {
  toolName: string;
  args: Record<string, unknown>;
}): boolean {
  if (!SIGNAL_HITL_TOOL_NAMES.has(toolName)) {
    return false;
  }
  if (args.operation === 'create') {
    return false;
  }
  const signalId = readSignalIdFromHitlArgs(args);
  return Boolean(signalId);
}

export function readSignalIdFromHitlArgs(
  args: Record<string, unknown>,
): string | undefined {
  if (typeof args.signalId === 'string' && args.signalId.trim()) {
    return args.signalId.trim();
  }
  if (Array.isArray(args.signals) && args.signals.length > 0) {
    const firstSignal = args.signals[0];
    if (
      firstSignal &&
      typeof firstSignal === 'object' &&
      typeof (firstSignal as Record<string, unknown>).signalId === 'string'
    ) {
      return (firstSignal as Record<string, unknown>).signalId as string;
    }
  }
  if (
    args.signal &&
    typeof args.signal === 'object' &&
    typeof (args.signal as Record<string, unknown>).signalId === 'string'
  ) {
    return (args.signal as Record<string, unknown>).signalId as string;
  }
  return undefined;
}

export function buildSignalDataFromDto(signal: GroupedSignal): SignalData {
  const metadata = readSignalMetadata(signal.metadata);
  return {
    operation: 'update',
    signalId: signal.id,
    title: signal.description ?? '',
    hypothesis: metadata.hypothesis ?? '',
    description: metadata.description ?? '',
    analysis: signal.analysis ?? '',
    category: signal.category,
    confidence: signal.confidence ?? undefined,
    reason: signal.reason ?? '',
    isAuthenticated: signal.isAuthenticated ?? false,
    stage: signal.stage,
    status: signal.status ?? undefined,
    endpointId: signal.endpointId ?? undefined,
    harEntryId: signal.harEntryId ?? undefined,
    entryPointId: signal.entryPointId ?? undefined,
    securityTestCaseSummary: signal.securityTestCaseSummary ?? undefined,
  };
}

function pickSignalField<K extends keyof SignalData>({
  proposed,
  existing,
  field,
}: {
  proposed: Partial<SignalData>;
  existing: SignalData;
  field: K;
}): SignalData[K] {
  const proposedValue = proposed[field];
  if (typeof proposedValue === 'string') {
    return (
      proposedValue.trim() ? proposedValue : existing[field]
    ) as SignalData[K];
  }
  if (proposedValue !== undefined && proposedValue !== null) {
    return proposedValue as SignalData[K];
  }
  return existing[field];
}

export function mergeSignalHitlData({
  proposed,
  existing,
}: {
  proposed: Partial<SignalData>;
  existing: SignalData;
}): SignalData {
  return {
    ...existing,
    operation: proposed.operation ?? existing.operation ?? 'update',
    signalId: proposed.signalId ?? existing.signalId,
    title: pickSignalField({ proposed, existing, field: 'title' }),
    hypothesis: pickSignalField({ proposed, existing, field: 'hypothesis' }),
    description: pickSignalField({ proposed, existing, field: 'description' }),
    analysis: pickSignalField({ proposed, existing, field: 'analysis' }),
    reason: pickSignalField({ proposed, existing, field: 'reason' }),
    category: pickSignalField({ proposed, existing, field: 'category' }),
    confidence: pickSignalField({ proposed, existing, field: 'confidence' }),
    stage: pickSignalField({ proposed, existing, field: 'stage' }),
    status: pickSignalField({ proposed, existing, field: 'status' }),
    isAuthenticated: pickSignalField({
      proposed,
      existing,
      field: 'isAuthenticated',
    }),
    endpointId: pickSignalField({ proposed, existing, field: 'endpointId' }),
    harEntryId: pickSignalField({ proposed, existing, field: 'harEntryId' }),
    entryPointId: pickSignalField({
      proposed,
      existing,
      field: 'entryPointId',
    }),
    securityTestCaseSummary: pickSignalField({
      proposed,
      existing,
      field: 'securityTestCaseSummary',
    }),
  };
}
